import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import type { Run } from "../../types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";

const config = {
  completed: { label: "Completed", color: "var(--chart-2)" },
  inFlight: { label: "In flight", color: "var(--chart-1)" },
  failed: { label: "Failed", color: "var(--chart-3)" },
} satisfies ChartConfig;

const HOURS = 24;

/** Runs started per hour over the last day, split by how they ended. */
export function ActivityChart({ runs }: { runs: Run[] }) {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  const start = now.getTime() - (HOURS - 1) * 3600_000;
  const buckets = Array.from({ length: HOURS }, (_, i) => ({
    hour: new Date(start + i * 3600_000).toLocaleTimeString([], { hour: "2-digit", hourCycle: "h23" }),
    completed: 0,
    inFlight: 0,
    failed: 0,
  }));
  for (const run of runs) {
    const index = Math.floor((new Date(run.createdAt).getTime() - start) / 3600_000);
    const bucket = buckets[index];
    if (!bucket) continue;
    if (run.status === "completed") bucket.completed++;
    else if (run.status === "failed" || run.status === "expired") bucket.failed++;
    else if (run.status !== "cancelled") bucket.inFlight++;
  }
  const total = buckets.reduce((n, b) => n + b.completed + b.inFlight + b.failed, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Run activity</CardTitle>
        <CardDescription>
          {total} station runs started in the last 24 hours on recent work orders
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="aspect-auto h-60 w-full">
          <BarChart data={buckets} margin={{ left: -20, right: 4 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="hour" tickLine={false} axisLine={false} tickMargin={8} interval={3} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent labelFormatter={(h) => `${h}:00`} />}
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar dataKey="completed" stackId="runs" fill="var(--color-completed)" />
            <Bar dataKey="inFlight" stackId="runs" fill="var(--color-inFlight)" />
            <Bar dataKey="failed" stackId="runs" fill="var(--color-failed)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
